import { memo } from "react";
import cx from "classnames";
import { useIsMobile } from "component-library";
import {
  LogoWhite,
  LogoBlack,
  LogoRoundedDark,
  FMLogoWhite,
  FMLogoDark,
  FMLogoRounded,
} from "assets";
import {
  checkIsYp,
  checkIsFilmmakersProfile,
  checkIsGraduate,
} from "shared-auth";
import { TFunction, useTranslation } from "i18n";
import { Theme } from "shared-types";
import { useGetProfileBasicInfoSource } from "pages/Profile/hooks/useGetProfileBasicInfoSource";
import { useProfileTheme } from "pages/Profile/hooks/useProfileTheme";

const getLabelForYP = (
  t: TFunction,
  isVisibleActualAge: boolean,
  age: string,
  isLicenseRequired: boolean
): string => {
  if (!isLicenseRequired) {
    if (isVisibleActualAge && age) {
      return t("performer:profile.header.ypAgeAndNoLicenceRequired", {
        age,
      });
    }

    return t("performer:profile.header.noLicenceRequired");
  }

  if (!isVisibleActualAge && isLicenseRequired) {
    return t("performer:profile.header.licenceRequired");
  }

  if (isVisibleActualAge && age && isLicenseRequired) {
    return t("performer:profile.header.ypAgeAndLicence", {
      age,
    });
  }

  return "";
};

const renderCommaSeparatedLines = (text: string) => {
  const parts = text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.map((part, index) => {
    const isLast = index === parts.length - 1;

    return (
      <span key={index} className="block">
        {isLast ? part : `${part},`}
      </span>
    );
  });
};

const formatYPSubLabel = (text: string, isScreenSizeSmall: boolean) => {
  return (
    <span
      className={cx(
        "block text-[10px] font-normal normal-case leading-tight text-color-neutral-two md:text-[11px] dark:text-white/70",
        {
          "text-right": isScreenSizeSmall,
        }
      )}
    >
      {isScreenSizeSmall ? renderCommaSeparatedLines(text) : text}
    </span>
  );
};

const ProfileLogos = () => {
  const { t } = useTranslation();
  const [theme] = useProfileTheme();
  const { rawPublicProfile, rawBasicInfo, isBasicInfoSourceLoading } =
    useGetProfileBasicInfoSource();

  const isDark = theme === Theme.Dark;

  const artistType = rawPublicProfile?.artistType;
  const isFilmmakers = checkIsFilmmakersProfile(artistType);
  const isYoungPerformer = checkIsYp(artistType);
  const isGraduate = checkIsGraduate(artistType);
  const isScreenSizeSmall = useIsMobile();

  const basicInfoForYp = rawBasicInfo as
    | {
        isVisibleActualAge?: boolean;
        age?: string;
        isLicenseRequired?: boolean;
      }
    | null
    | undefined;

  const ypSubLabel =
    basicInfoForYp &&
    getLabelForYP(
      t,
      basicInfoForYp.isVisibleActualAge ?? false,
      basicInfoForYp.age ?? "",
      basicInfoForYp.isLicenseRequired ?? false
    );

  const showYpSubLabel =
    isYoungPerformer && !isBasicInfoSourceLoading && ypSubLabel;
  const showLabel = !isFilmmakers && (isGraduate || isYoungPerformer);
  const labelText = isGraduate
    ? t("common:performer.type.graduate")
    : isYoungPerformer
      ? t("common:performer.type.youngPerformer")
      : null;

  const labelLines =
    labelText != null ? labelText.split(/\s+/).filter(Boolean) : [];

  if (isBasicInfoSourceLoading) {
    return null;
  }

  return (
    <span className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
      <span className="flex shrink-0 items-start">
        {isFilmmakers ? (
          <>
            {isDark ? (
              <FMLogoWhite
                width={210}
                height={48}
                className="ml-auto hidden lg:block"
              />
            ) : (
              <FMLogoDark
                width={210}
                height={48}
                className="ml-auto hidden lg:block"
              />
            )}
            <FMLogoRounded
              width={64}
              height={64}
              className="ml-auto block lg:hidden"
            />
          </>
        ) : (
          <>
            {isDark ? (
              <LogoWhite width={210} className="ml-auto hidden lg:block" />
            ) : (
              <LogoBlack width={210} className="ml-auto hidden lg:block" />
            )}
            <LogoRoundedDark
              width={64}
              height={64}
              className="ml-auto block lg:hidden"
            />
          </>
        )}
      </span>
      {showLabel && labelLines.length > 0 && (
        <span
          className={cx(
            "flex flex-col items-end gap-0.5 text-center md:w-auto md:text-right",
            isYoungPerformer ? "w-[220px]" : "w-[66px]"
          )}
        >
          <span className="hidden text-[12px] font-normal uppercase leading-normal text-color-neutral-one md:block dark:text-white/90">
            {labelText}
          </span>
          <span className="block text-[12px] font-normal uppercase leading-normal text-color-neutral-one md:hidden dark:text-white/90">
            {labelLines.length === 1 ? (
              labelLines[0]
            ) : (
              <span className="block">
                {labelLines.map((word, i) => (
                  <span key={i} className="block">
                    {word}
                  </span>
                ))}
              </span>
            )}
          </span>
          {showYpSubLabel && formatYPSubLabel(ypSubLabel, isScreenSizeSmall)}
        </span>
      )}
    </span>
  );
};

export default memo(ProfileLogos);
